/**
 * Passkeys are the only credential. There is no password, no email and no
 * guest: an account exists the moment a passkey does.
 *
 * Every ceremony is begin → browser → finish, and `finish` is what issues the
 * session token. The one thing worth being careful about is *detecting* that
 * WebAuthn will not work before asking the player for anything — see
 * `webAuthnAvailability`, which is called on the landing page.
 */
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import type { AuthResult, DeviceLink, LinkPeek } from '@cards/shared'
import { api, setSession } from './session'

export type { AuthResult, DeviceLink, LinkPeek }

interface BeginRegistration {
  readonly options: PublicKeyCredentialCreationOptionsJSON
  readonly challengeId: string
}

interface BeginLogin {
  readonly options: PublicKeyCredentialRequestOptionsJSON
  readonly challengeId: string
}

export type WebAuthnAvailability =
  | { readonly kind: 'ok' }
  /** In-app browsers are where a friend's link most often opens and where
   *  passkeys most often fail; the fix is "open this in Safari/Chrome", so it is
   *  worth telling them apart from a genuinely unsupported browser. */
  | { readonly kind: 'inAppBrowser'; readonly app: string }
  | { readonly kind: 'unsupported' }

const IN_APP_PATTERNS: readonly (readonly [RegExp, string])[] = [
  [/FBAN|FBAV|FB_IAB/i, 'Facebook'],
  [/Instagram/i, 'Instagram'],
  [/Messenger/i, 'Messenger'],
  [/\bLine\//i, 'LINE'],
  [/TikTok|musical_ly|BytedanceWebview/i, 'TikTok'],
  [/GSA\//i, 'the Google app'],
  [/Twitter/i, 'X'],
  [/Snapchat/i, 'Snapchat'],
  [/WhatsApp/i, 'WhatsApp'],
  [/LinkedInApp/i, 'LinkedIn'],
]

export function webAuthnAvailability(ua: string = navigator.userAgent): WebAuthnAvailability {
  for (const [pattern, app] of IN_APP_PATTERNS) {
    if (pattern.test(ua)) return { kind: 'inAppBrowser', app }
  }
  if (typeof window === 'undefined' || !window.PublicKeyCredential || !browserSupportsWebAuthn()) {
    return { kind: 'unsupported' }
  }
  return { kind: 'ok' }
}

export async function supportsAutofill(): Promise<boolean> {
  try {
    return await browserSupportsWebAuthnAutofill()
  } catch {
    return false
  }
}

export async function createAccount(displayName: string): Promise<AuthResult> {
  const begin = await api<BeginRegistration>('/api/auth/register/begin', {
    method: 'POST',
    body: JSON.stringify({ displayName }),
  })
  const response = await startRegistration({ optionsJSON: begin.options })
  const result = await api<AuthResult>('/api/auth/register/finish', {
    method: 'POST',
    body: JSON.stringify({ challengeId: begin.challengeId, response }),
  })
  setSession(result.sessionToken, result.user)
  return result
}

/** `conditional` runs the discoverable-credential autofill prompt instead of a
 *  modal, so a returning player signs in with one tap on the landing page. */
export async function signIn(conditional = false): Promise<AuthResult> {
  const begin = await api<BeginLogin>('/api/auth/login/begin', { method: 'POST', body: '{}' })
  const response = await startAuthentication({
    optionsJSON: begin.options,
    useBrowserAutofill: conditional,
    verifyBrowserAutofillInput: false,
  })
  const result = await api<AuthResult>('/api/auth/login/finish', {
    method: 'POST',
    body: JSON.stringify({ challengeId: begin.challengeId, response }),
  })
  setSession(result.sessionToken, result.user)
  return result
}

export async function peekLink(token: string): Promise<LinkPeek> {
  return api<LinkPeek>(`/api/auth/link/peek?token=${encodeURIComponent(token)}`)
}

/** Second device via a link: the token authorises registering a *new* passkey
 *  for an existing account. Platform sync and the QR hybrid transport both
 *  handle this with no code; this is the cross-ecosystem fallback. */
export async function redeemLink(token: string): Promise<AuthResult> {
  const begin = await api<BeginRegistration>('/api/auth/link/redeem/begin', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
  const response = await startRegistration({ optionsJSON: begin.options })
  const result = await api<AuthResult>('/api/auth/link/redeem/finish', {
    method: 'POST',
    body: JSON.stringify({ token, challengeId: begin.challengeId, response }),
  })
  setSession(result.sessionToken, result.user)
  return result
}

/**
 * Mint the link. `link/create/begin` answers with *authentication* options, not
 * registration ones: a token that can add a passkey to the account requires a
 * fresh assertion, not merely a live session, so the ceremony has to be run and
 * its `response` posted to `finish`. It was typed as a registration and the
 * assertion was never run, so `finish` saw a body with no `response` and
 * answered 400 every time — and with passkeys the only credential and no
 * recovery path, this is the one cross-ecosystem route a player has (VERDICT
 * H5). The 400 also consumed the challenge, so retrying could not work either.
 */
export async function createDeviceLink(): Promise<DeviceLink> {
  const begin = await api<BeginLogin>('/api/auth/link/create/begin', {
    method: 'POST',
    body: '{}',
  })
  const response = await startAuthentication({ optionsJSON: begin.options })
  return api<DeviceLink>('/api/auth/link/create/finish', {
    method: 'POST',
    body: JSON.stringify({ challengeId: begin.challengeId, response }),
  })
}
